import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

declare const _COLUMNS_DIMENSION_PROPERTIES: readonly [
  'maxWidth',
  'minWidth',
  'width',
  'flex'
];

enum GridPreferencePanelsValue {
  filters = 'filters',
  columns = 'columns',
}

interface GridPreferencePanelState {
  open: boolean;
  openedPanelValue?: GridPreferencePanelsValue;
}
type GridPreferencePanelInitialState = GridPreferencePanelState;

type GridRowId = string | number;
export type GridColumnVisibilityModel = Record<GridRowId, boolean>;
type GridColumnDimensionProperties =
  (typeof _COLUMNS_DIMENSION_PROPERTIES)[number];

type GridColumnDimensions = {
  [key in GridColumnDimensionProperties]?: number;
};

declare enum GridLogicOperator {
  And = 'and',
  Or = 'or',
}

interface GridFilterItem {
  id?: number | string;
  field: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  operator: string;
}

interface GridFilterModel {
  items: GridFilterItem[];
  logicOperator?: GridLogicOperator;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  quickFilterValues?: any[];
  quickFilterLogicOperator?: GridLogicOperator;
}

interface GridSortItem {
  field: string;
  sort: 'asc' | 'desc' | null | undefined;
}
export type GridSortModel = GridSortItem[];

export type GridTimeModel = {
  start_time?: string;
  end_time?: string;
  duration?: string;
};

export interface GridPaginationModel {
  pageSize: number;
  pageIndex: number;
}

interface GridPaginationInitialState {
  paginationModel?: Partial<GridPaginationModel>;
}

interface GridSortingInitialState {
  sortModel?: GridSortModel;
}

interface GridFilterInitialState {
  filterModel?: GridFilterModel;
}

interface GridColumnsInitialState {
  columnVisibilityModel?: GridColumnVisibilityModel;
  orderedFields?: string[];
  dimensions?: Record<string, GridColumnDimensions>;
}

interface GridInitialStateCommunity {
  pagination?: GridPaginationInitialState;
  sorting?: GridSortingInitialState;
  filter?: GridFilterInitialState;
  columns?: GridColumnsInitialState;
  preferencePanel?: GridPreferencePanelInitialState;
}

export function tryJsonParseSearchParam<T>(
  searchParams: URLSearchParams,
  key: string,
  defaultValue: T
): T {
  const param = searchParams.get(key);
  if (param) {
    try {
      return JSON.parse(param);
    } catch (e) {
      return defaultValue;
    }
  } else {
    return defaultValue;
  }
}

const PAGINATION_MODEL_DEFAULT: GridPaginationModel = {
  pageIndex: 0,
  pageSize: 15,
};

const SORT_MODEL_DEFAULT: GridSortModel = [];

const FILTER_MODEL_DEFAULT: GridFilterModel = {
  items: [],
};

const TIME_MODEL_DEFAULT: GridTimeModel = {};

const COLUMN_VISIBILITY_MODEL_DEFAULT: GridColumnVisibilityModel = {};

export interface DataGridStateManagement {
  paginationModel: GridPaginationModel;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
  resetPaginationModel: () => void;
  sortModel: GridSortModel;
  setSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: React.Dispatch<
    React.SetStateAction<GridColumnVisibilityModel>
  >;
  onColumnVisibilityResetClick?: () => void;
  filterModel: GridFilterModel;
  setFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>;
  timeModel: GridTimeModel;
  setTimeModel: React.Dispatch<React.SetStateAction<GridTimeModel>>;
  initialState: GridInitialStateCommunity;
}

export function useDataGridState({
  defaultTimeModel,
  defaultColumnVisibility,
  columnVisibilityStateKey,
  updateSearchParams,
  defaultPaginationModel,
}: {
  defaultTimeModel?: GridTimeModel;
  defaultColumnVisibility?: GridColumnVisibilityModel;
  columnVisibilityStateKey?: `ls:ui:${string}`;
  updateSearchParams?: (params: URLSearchParams) => void;
  defaultPaginationModel?: GridPaginationModel;
}): DataGridStateManagement {
  const [searchParams, setSearchParams] = useSearchParams();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    tryJsonParseSearchParam(
      searchParams,
      'paginationModel',
      defaultPaginationModel ?? PAGINATION_MODEL_DEFAULT
    )
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(() =>
    tryJsonParseSearchParam(searchParams, 'sortModel', SORT_MODEL_DEFAULT)
  );

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(() => {
      let storageVisibility: GridColumnVisibilityModel | null = null;
      if (columnVisibilityStateKey != null) {
        try {
          storageVisibility = JSON.parse(
            localStorage.getItem(columnVisibilityStateKey) ?? ''
          );
        } catch {
          // ignore
        }
      }

      return tryJsonParseSearchParam(
        searchParams,
        'columnVisibilityModel',
        storageVisibility ||
          defaultColumnVisibility ||
          COLUMN_VISIBILITY_MODEL_DEFAULT
      );
    });

  const [filterModel, _setFilterModel] = useState<GridFilterModel>(() =>
    tryJsonParseSearchParam(searchParams, 'filterModel', FILTER_MODEL_DEFAULT)
  );

  const [timeModel, setTimeModel] = useState<GridTimeModel>(() =>
    tryJsonParseSearchParam(
      searchParams,
      'timeModel',
      defaultTimeModel ?? TIME_MODEL_DEFAULT
    )
  );

  const resetPaginationModel = useCallback(() => {
    setPaginationModel((model) => ({ ...model, pageIndex: 0 }));
  }, []);

  const setFilterModel: typeof _setFilterModel = useCallback(
    (arg) => {
      resetPaginationModel();
      return _setFilterModel(arg);
    },
    [resetPaginationModel]
  );

  // Save the current state in the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // 1. Update query params with the models we manage here
    if (JSON.stringify(sortModel) !== JSON.stringify(SORT_MODEL_DEFAULT)) {
      params.set('sortModel', JSON.stringify(sortModel));
    } else {
      params.delete('sortModel');
    }

    if (columnVisibilityStateKey != null) {
      localStorage.setItem(
        columnVisibilityStateKey,
        JSON.stringify(columnVisibilityModel)
      );
    }

    if (
      JSON.stringify(columnVisibilityModel) !==
      JSON.stringify(defaultColumnVisibility || COLUMN_VISIBILITY_MODEL_DEFAULT)
    ) {
      params.set(
        'columnVisibilityModel',
        JSON.stringify(columnVisibilityModel)
      );
    } else {
      params.delete('columnVisibilityModel');
    }

    if (
      JSON.stringify(paginationModel) !==
      JSON.stringify(PAGINATION_MODEL_DEFAULT)
    ) {
      params.set('paginationModel', JSON.stringify(paginationModel));
    } else {
      params.delete('paginationModel');
    }
    if (JSON.stringify(filterModel) !== JSON.stringify(FILTER_MODEL_DEFAULT)) {
      params.set('filterModel', JSON.stringify(filterModel));
    } else {
      params.delete('filterModel');
    }
    if (JSON.stringify(timeModel) !== JSON.stringify({})) {
      params.set('timeModel', JSON.stringify(timeModel));
    } else {
      params.delete('timeModel');
    }

    // 2. Update query params with any additional params
    updateSearchParams?.(params);

    // 3. Update the URL
    setSearchParams(params, { replace: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationModel,
    filterModel,
    sortModel,
    timeModel,
    columnVisibilityModel,
    updateSearchParams,
  ]);

  const initialState = useMemo<GridInitialStateCommunity>(
    () => ({
      columns: { columnVisibilityModel },
      pagination: { paginationModel },
      filter: { filterModel },
      sorting: { sortModel },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onColumnVisibilityResetClick = useMemo(() => {
    if (
      JSON.stringify(columnVisibilityModel) !==
      JSON.stringify(defaultColumnVisibility || COLUMN_VISIBILITY_MODEL_DEFAULT)
    ) {
      return () => {
        setColumnVisibilityModel(
          defaultColumnVisibility || COLUMN_VISIBILITY_MODEL_DEFAULT
        );
      };
    }
    return undefined;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnVisibilityModel]);

  return {
    paginationModel,
    setPaginationModel,
    resetPaginationModel,
    sortModel,
    setSortModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    onColumnVisibilityResetClick,
    filterModel,
    setFilterModel,
    timeModel,
    setTimeModel,
    initialState,
  };
}
